import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// ConversationCard + ConversationList (design/components/inbox.html, col 1).
// Summarises a conversation with a priority bar, chips and who's handling it.
// Priority/assignment are chip + text (never the colour bar alone). The active
// conversation exposes aria-current; rows are real buttons.

const priorityCfg = {
  prio: {
    bar: 'bg-bar-prio',
    chip: 'bg-warn-bg text-warn',
    labelKey: 'ui.conversation.priorityHigh',
  },
  sens: {
    bar: 'bg-bar-sens',
    chip: 'bg-sens-bg text-sens',
    labelKey: 'ui.conversation.prioritySensitive',
  },
} as const

const assignmentLabelKey = {
  you: 'ui.conversation.assignmentYou',
  bot: 'ui.conversation.assignmentBot',
  unassigned: 'ui.conversation.assignmentUnassigned',
  // 'other' = a COLLEAGUE is already on it. Named when we know the name, so the
  // row says "Con Ana García" rather than a bare state (CP-1 F-C).
  other: 'ui.conversation.assignmentOther',
} as const

export interface ConversationCardProps {
  contact: { name: string; initials: string }
  snippet: string
  time: string
  unread?: boolean
  priority?: 'prio' | 'sens'
  assignment?: 'you' | 'bot' | 'unassigned' | 'other'
  /** Who is holding it — rendered with `assignment="other"`. Without it the row
   *  falls back to the unnamed "Con otro asesor". */
  holderName?: string
  active?: boolean
  onClick?: () => void
}

export function ConversationCard({
  contact,
  snippet,
  time,
  unread,
  priority,
  assignment,
  holderName,
  active,
  onClick,
}: ConversationCardProps) {
  const { t } = useTranslation('components')
  const prio = priority ? priorityCfg[priority] : null
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'relative flex w-full gap-[11px] border-b border-line py-3 pl-4 pr-[14px] text-left transition-colors',
          'focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-blue',
          active ? 'bg-[color-mix(in_srgb,var(--brand-blue)_6%,transparent)]' : 'hover:bg-hover',
        )}
      >
        {prio && (
          <span
            aria-hidden
            className={cn('absolute bottom-3 left-0 top-3 w-[3px] rounded-r-[3px]', prio.bar)}
          />
        )}
        <span
          aria-hidden
          className="flex size-10 flex-none items-center justify-center rounded-full bg-field text-[13px] font-semibold text-text-2"
        >
          {contact.initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span
              className={cn(
                'truncate text-[13px] text-text',
                unread ? 'font-bold' : 'font-semibold',
              )}
            >
              {contact.name}
            </span>
            <span className="ml-auto flex-none text-[10px] text-text-3">{time}</span>
          </span>
          <span
            className={cn(
              'mt-0.5 block truncate text-[11.5px]',
              unread ? 'font-medium text-text' : 'text-text-2',
            )}
          >
            {snippet}
          </span>
          {(prio != null || assignment != null) && (
            <span className="mt-1.5 flex items-center gap-1.5">
              {prio && (
                <span className={cn('rounded-[5px] px-1.5 py-0.5 text-[9px] font-bold', prio.chip)}>
                  {t(prio.labelKey)}
                </span>
              )}
              {assignment === 'you' && (
                <span className="rounded-pill bg-[color-mix(in_srgb,var(--brand-blue)_10%,transparent)] px-[7px] py-0.5 text-[9px] font-bold text-link">
                  {t('ui.conversation.assignmentYou')}
                </span>
              )}
              {assignment && assignment !== 'you' && (
                <span className="text-[9.5px] font-medium text-text-3">
                  {assignment === 'other' && holderName
                    ? t('ui.conversation.assignmentOtherNamed', { name: holderName })
                    : t(assignmentLabelKey[assignment])}
                </span>
              )}
            </span>
          )}
        </span>
      </button>
    </li>
  )
}

export interface ConversationListProps {
  children: ReactNode
  /** Accessible name for the conversation queue. */
  label?: string
  /** Count shown in the header chip. */
  count?: number
  /** Filter tabs row (e.g. Todas / Contigo / Sensibles). */
  tabs?: ReactNode
  className?: string
}

/** Scrollable Bandeja queue shell: header with count chip, optional filter
 *  tabs row, and a labelled <ul> whose children are ConversationCard rows. */
export function ConversationList({
  children,
  label,
  count,
  tabs,
  className,
}: ConversationListProps) {
  const { t } = useTranslation('components')
  const resolvedLabel = label ?? t('ui.conversationList')
  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className="flex items-center gap-[9px] px-[15px] py-[13px]">
        <span className="text-[14px] font-bold text-text">{resolvedLabel}</span>
        {count != null && (
          <span className="ml-auto rounded-pill bg-field px-2 py-px text-[10.5px] font-bold tabular-nums text-text-2">
            {count}
          </span>
        )}
      </div>
      {tabs && (
        <div className="flex gap-1.5 overflow-x-auto border-b border-line px-[14px] pb-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {tabs}
        </div>
      )}
      <ul aria-label={resolvedLabel} className="flex-1 overflow-y-auto overflow-x-hidden">
        {children}
      </ul>
    </div>
  )
}
